import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Grid,
  CircularProgress,
  Checkbox,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  // TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FolderOutlined as FolderOutlinedIcon,
  DashboardCustomizeOutlined as DashboardCustomizeOutlinedIcon,
  SearchOutlined,
  Delete as DeleteIcon,
  // LocalOffer as LocalOfferIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useAppSelector } from "@/app/hooks";
import { selectApiData } from "@/features/bughurd/boardSelector";
import {
  useGetListOfBugsQuery,
  useGetListOfProjectsQuery,
  useGetListOfMembersQuery,
  useDeleteBugMutation,
  useUpdateBugDetailMutation,
} from "@/features/bughurd/api";
import { toast } from "react-toastify";
import DateTimeRangeFilter from "./DateTimeRangeFilter";
import { hexToRgba } from "../../../../Components/helpers/util";
import {
  addRecentProject,
  getRecentProjects,
  colorPalette,
  getInitials,
} from "../../utils";
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-pro";
import { useStyles } from "../../../../Components/Common/styles";
import { useCustomLocaleText } from "../../../../Components/Common/useCustomLocaleText";
import useDynamicHeight from "../../../../Components/scrollable/useDynamicHeight";
import { TabContext, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import LiveFeed from "./LiveFeed";

interface SystemBugListProps {
  parentData?: any;
}

// Map severity to level - defined outside component to avoid reference errors
const severityToLevel: Record<string, string> = {
  // Highest: "Critical",
  High: "Error",
  Medium: "Warning",
  Low: "Info",
};

const SystemBugList: React.FC<SystemBugListProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["bugherd"]);
  const { id: projectId } = useParams<{ id: string }>();
  const theme = useTheme();
  const classes = useStyles();
  const localeText = useCustomLocaleText();
  const dynamicHeight = useDynamicHeight();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { companyDetails } = useAppSelector((state) => state.appGlobal);
  const { projectDetails } = useAppSelector(selectApiData);
  const [topTabValue, setTopTabValue] = useState(0);

  // Live Feed filters
  const [groupByFilter, setGroupByFilter] = useState<string>("");

  // Project selector state
  const [projectAnchorEl, setProjectAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [projectSearch, setProjectSearch] = useState("");
  const projectListRef = useRef<HTMLDivElement>(null);
  const openProjectMenu = Boolean(projectAnchorEl);

  // Fetch projects list
  // API Calls
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
  } = useGetListOfProjectsQuery({
    searchName: projectSearch,
    page: 1,
    limit: 20,
  });

  const projects = projectsData?.data?.decryptedData?.items || [];

  // Menu items for project selector
  const menuItems = [
    {
      name: t("My Projects"),
      icon: <FolderOutlinedIcon />,
      disabled: false,
    },
    {
      name: t("Dashboard"),
      icon: <DashboardCustomizeOutlinedIcon />,
      disabled: true,
    },
  ];

  // Date range state for filtering
  const [updatedAtStart, setUpdatedAtStart] = useState<string | null>(null);
  const [updatedAtEnd, setUpdatedAtEnd] = useState<string | null>(null);

  // Selected filter state (for API params)
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  // const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Table search state
  const [tableSearch, setTableSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Selected rows for bulk delete
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Delete mutation
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();

  // Update bug mutation for Add to Board
  const [updateBug, { isLoading: isAddingToBoard }] =
    useUpdateBugDetailMutation();

  // Action menu anchor
  const [actionAnchorEl, setActionAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 10,
    page: 1,
  });

  // Menu anchor states
  const [ownerAnchorEl, setOwnerAnchorEl] = useState<HTMLElement | null>(null);
  const [levelAnchorEl, setLevelAnchorEl] = useState<HTMLElement | null>(null);
  // const [groupByAnchorEl, setGroupByAnchorEl] = useState<HTMLElement | null>(null);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  // const [tagsAnchorEl, setTagsAnchorEl] = useState<HTMLElement | null>(null);

  // Fetch project members for owner filter
  const { data: projectMembersData } = useGetListOfMembersQuery({
    projectId: projectId,
    page: 1,
    size: 100,
    searchName: "",
  });

  const projectMembers = useMemo(() => {
    const memberItems = projectMembersData?.data?.decryptedData?.items || [];
    // Filter by role and map to required format
    return memberItems
      .filter(
        (item: any) =>
          item?.userRole === "TEAM_MEMBER" || item?.userRole === "TEAM_LEAD",
      )
      .map((item: any) => ({
        _id: item?.user?._id || item?._id,
        fullName:
          `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
        email: item?.user?.email,
        userRole: item?.userRole,
      }));
  }, [projectMembersData]);

  // Fetch Live Logs with AUTO_BUG filter
  const {
    data: systemBugsData,
    isLoading: isBugsLoading,
    isFetching: isBugsFetching,
    refetch: refetchBugs,
  } = useGetListOfBugsQuery(
    {
      id: projectId,
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      search: tableSearch || undefined,
      bugFor: "AUTO_BUG",
      withDetails: false,
      ...(selectedOwners.length > 0 && { assignTo: selectedOwners }),
      ...(selectedLevels.length > 0 && {
        severities: selectedLevels.map(
          (level) =>
            Object.keys(severityToLevel).find(
              (key) => severityToLevel[key] === level,
            ) || level,
        ),
      }),
      // ...(selectedTags.length > 0 && { tags: selectedTags }),
      ...(updatedAtStart && { updatedAtStart: updatedAtStart.split("T")[0] }),
      ...(updatedAtEnd && { updatedAtEnd: updatedAtEnd.split("T")[0] }),
    },
    { skip: !projectId },
  );

  const systemBugs = useMemo(() => {
    const items =
      systemBugsData?.data?.decryptedData?.items ||
      systemBugsData?.data?.items ||
      [];
    return items;
  }, [systemBugsData]);

  const totalCount =
    systemBugsData?.data?.decryptedData?.total ||
    systemBugsData?.data?.total ||
    0;

  // Project selector handlers
  const handleProjectClick = (event: React.MouseEvent<HTMLElement>) => {
    setProjectAnchorEl(event.currentTarget);
  };

  const handleProjectClose = () => {
    setProjectAnchorEl(null);
    setProjectSearch("");
  };

  const handleProjectSelect = (project: any) => {
    handleProjectClose();
    if (project === "My Projects") {
      navigate(`/defect-tracker/projects`);
    } else {
      addRecentProject(project);
      navigate(`/defect-tracker/projects/${project._id}/system-bugs`);
    }
  };

  // Get non-recent projects
  const getNonRecentProjects = () => {
    const recentIds = getRecentProjects().map((item) => item._id);
    return projects.filter((project: any) => !recentIds.includes(project._id));
  };

  // Extract unique values for filters from data
  const filterOptions = useMemo(() => {
    const projects = Array.from(
      new Set(systemBugs.map((bug) => bug.project?.name).filter(Boolean)),
    );
    const environments = ["test", "production", "development"];
    const frameworks = ["JS"];
    const levels = ["Error", "Warning", "Info", "Debug"];
    // Extract unique tags from all bugs
    const tags = Array.from(
      new Set(systemBugs.flatMap((bug) => bug.tags || []).filter(Boolean)),
    ) as string[];

    return { projects, environments, frameworks, levels, tags };
  }, [systemBugs]);

  // Handle owner filter change
  const handleOwnerFilterChange = (ownerId: string) => {
    setSelectedOwners((prev) =>
      prev.includes(ownerId)
        ? prev.filter((id) => id !== ownerId)
        : [...prev, ownerId],
    );
  };

  // Handle level filter change
  const handleLevelFilterChange = (level: string) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  };

  // Handle date range filter
  const handleDateRangeApply = (start: string | null, end: string | null) => {
    setUpdatedAtStart(start);
    setUpdatedAtEnd(end);
  };

  // Handle date range clear
  const handleDateRangeClear = () => {
    setUpdatedAtStart(null);
    setUpdatedAtEnd(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchBugs();
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    setActionAnchorEl(null);

    try {
      const deletePromises = selectedRows.map((bugId) => deleteBug(bugId));
      await Promise.all(deletePromises);
      toast.success(`Successfully deleted ${selectedRows.length} bug(s)`);
      setSelectedRows([]);
      refetchBugs();
    } catch {
      toast.error("Failed to delete some bugs");
    }
  };

  // Handle Add to Board
  const handleAddToBoard = async () => {
    if (selectedRows.length === 0) return;
    setActionAnchorEl(null);

    try {
      const updatePromises = selectedRows.map((bugId) =>
        updateBug({ bugId, bugFor: "BOARD" }),
      );
      await Promise.all(updatePromises);
      toast.success(
        `Successfully added ${selectedRows.length} bug(s) to board`,
      );
      setSelectedRows([]);
      refetchBugs();
    } catch {
      toast.error("Failed to add some bugs to board");
    }
  };

  // Handle row click to navigate to detail view
  const handleRowClick = (bugId: string) => {
    navigate(`/defect-tracker/projects/${projectId}/system-bugs/${bugId}`);
  };

  // Get level color
  const getLevelColor = (severity: string) => {
    const level = severityToLevel[severity] || "Info";
    switch (level) {
      case "Error":
        return "#D91656";
      case "Warning":
        return "#FFB200";
      case "Info":
        return "#2196F3";
      case "Debug":
        return "#9E9E9E";
      default:
        return "#9E9E9E";
    }
  };

  // Search handler with debounce
  const requestSearch = useCallback(
    (val: string) => {
      setSearchValue(val);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setTableSearch(val);
      }, 400);
    },
    [setTableSearch],
  );

  const handleTopTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setTopTabValue(newValue);
  };

  // Reset filters when switching tabs
  React.useEffect(() => {
    if (topTabValue === 0) {
      // Reset Live Feed filters when switching to Record tab
      setGroupByFilter("");
    } else if (topTabValue === 1) {
      // Reset Record filters when switching to Live Feed tab
      setSelectedOwners([]);
      setSelectedLevels([]);
      setUpdatedAtStart(null);
      setUpdatedAtEnd(null);
    }
  }, [topTabValue]);

  // Handle Group By filter change
  // const handleGroupByFilterChange = (value: string) => {
  //   setGroupByFilter(value);
  //   setGroupByAnchorEl(null);
  // };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: "level",
      headerName: t("level"),
      headerClassName: classes.customTableHeader,
      width: 100,
      renderCell: (params) => {
        const level = severityToLevel[params.row.severity] || "Info";
        const levelColor = getLevelColor(params.row.severity);
        return (
          <Chip
            label={level}
            size="small"
            sx={{
              bgcolor: levelColor,
              color: "white",
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          />
        );
      },
    },
    {
      field: "summary",
      headerName: t("itemTitle"),
      headerClassName: classes.customTableHeader,
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: "#FFA500",
              fontSize: "0.75rem",
              flexShrink: 0,
            }}
          >
            JS
          </Avatar>
          <Tooltip title={params.row.summary}>
            <Box sx={{ overflow: "hidden", flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {params.row.bugKey}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {params.row.summary}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "count",
      headerName: t("totalCount"),
      headerClassName: classes.customTableHeader,
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2">{params.row.count}</Typography>
      ),
    },
    // {
    //   field: "ipAddress",
    //   headerName: t("ipAddress"),
    //   headerClassName: classes.customTableHeader,
    //   width: 120,
    //   align: "right",
    //   headerAlign: "right",
    //   renderCell: (params) => (
    //     <Typography variant="body2">{params.row.ipAddress}</Typography>
    //   ),
    // },
    // "updatedAt": "2026-01-28T05:40:21.066Z", update need to calculate
    {
      field: "last",
      headerName: t("last"),
      headerClassName: classes.customTableHeader,
      width: 80,
      renderCell: (params) => {
        const updatedAt = new Date(params.row.updatedAt);
        const now = new Date();
        const diffInMinutes = Math.floor(
          (now.getTime() - updatedAt.getTime()) / 60000,
        );
        return (
          <Typography variant="body2" color="text.secondary">
            {diffInMinutes}m
          </Typography>
        );
      },
    },

    {
      field: "assignTo",
      headerName: t("owner"),
      headerClassName: classes.customTableHeader,
      width: 130,
      renderCell: (params) => {
        const assignees = params.value || [];
        if (assignees.length === 0) {
          return (
            <Chip
              label={t("Unassigned")}
              size="small"
              variant="outlined"
              icon={<PeopleIcon />}
            />
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {assignees.slice(0, 3).map((user: any, index: number) => (
              <Tooltip
                key={user._id || index}
                title={user.fullName || t("Unknown")}
                arrow
              >
                <Avatar
                  sx={{
                    backgroundColor: colorPalette[index % colorPalette.length],
                    width: 25,
                    height: 25,
                    fontSize: 10,
                    fontWeight: 500,
                    marginRight: "-4px",
                    border: "1px solid white",
                    zIndex: 10 - index,
                  }}
                >
                  {getInitials(user.fullName || "")}
                </Avatar>
              </Tooltip>
            ))}
            {assignees.length > 3 && (
              <Avatar
                sx={{
                  backgroundColor: "#ccc",
                  width: 25,
                  height: 25,
                  fontSize: 10,
                  fontWeight: 500,
                  border: "1px solid white",
                }}
              >
                +{assignees.length - 3}
              </Avatar>
            )}
          </Box>
        );
      },
    },
    // isSnoozed: boolean
    {
      field: "snooze",
      headerName: t("snooze"),
      headerClassName: classes.customTableHeader,
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.isSnoozed ? t("yes") : t("no")}
        </Typography>
      ),
    },
  ];

  // Custom toolbar component
  const GridCustomToolbar = (props: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const searchInputRef = useRef<HTMLInputElement>(null);

    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          value={props.value}
          onChange={props.onChange}
          placeholder={t("Search Bugs...")}
          inputRef={searchInputRef}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
          sx={{
            fieldset: { border: "none" },
          }}
        />
      </GridToolbarContainer>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflowX: "auto",
        overflowY: "hidden",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Top Header Bar - Similar to TaskLayout */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #d6d6d680",
          minHeight: "60px",
          px: 3,
        }}
      >
        {/* Left - Project Name (Clickable) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              color: "primary.main",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            onClick={handleProjectClick}
          >
            <Box component="span" sx={{ color: "#666" }}>
              &gt;
            </Box>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                backgroundColor:
                  projectDetails?.icon || theme.palette.primary.main,
                fontSize: "12px",
              }}
            >
              {projectDetails?.projectKey?.slice(0, 2) || "SB"}
            </Avatar>
            <Box component="span" sx={{ color: "#666" }}>
              {companyDetails?.companyName}
            </Box>
            <Box component="span" sx={{ color: "#000", fontWeight: 600 }}>
              {projectDetails?.name || "Live Logs"}
            </Box>
          </Typography>
        </Box>
        {/* Right - Filters Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          {/* Record Tab Filters */}
          {topTabValue === 0 && (
            <>
              <DateTimeRangeFilter
                onApply={handleDateRangeApply}
                onClear={handleDateRangeClear}
              />

              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setOwnerAnchorEl(e.currentTarget)}
                endIcon={<ArrowDropDownIcon />}
                startIcon={<PeopleIcon sx={{ fontSize: "16px" }} />}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  color: theme.palette.text.primary,
                  borderColor: "#ddd",
                  "&:hover": {
                    borderColor: "#aaa",
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                {t("owner")}
                {selectedOwners.length > 0 && (
                  <Typography component="span" sx={{ ml: 0.5, fontSize: "12px" }}>
                    ({selectedOwners.length})
                  </Typography>
                )}
              </Button>

              {/* Level Filter Dropdown */}
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setLevelAnchorEl(e.currentTarget)}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  color: theme.palette.text.primary,
                  borderColor: "#ddd",
                  "&:hover": {
                    borderColor: "#aaa",
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                {t("Level")}
                {selectedLevels.length > 0 && (
                  <Typography component="span" sx={{ ml: 0.5, fontSize: "12px" }}>
                    ({selectedLevels.length}/5)
                  </Typography>
                )}
              </Button>

              {/* Action Button - shown when rows are selected */}
              {selectedRows.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => setActionAnchorEl(e.currentTarget)}
                  disabled={isDeleting || isAddingToBoard}
                  endIcon={
                    isDeleting || isAddingToBoard ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ArrowDropDownIcon />
                    )
                  }
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    ml: 1,
                  }}
                >
                  Action ({selectedRows.length})
                </Button>
              )}
            </>
          )}

          {/* Live Feed Tab Filters */}
          {/* {topTabValue === 1 && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setGroupByAnchorEl(e.currentTarget)}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                color: theme.palette.text.primary,
                borderColor: "#ddd",
                minWidth: "140px",
                "&:hover": {
                  borderColor: "#aaa",
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              {t("Group By")}: {groupByFilter || t("None")}
            </Button>
          )} */}

          {/* Refresh Button - Always visible */}
          <Tooltip title={t("refresh")}>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isBugsFetching}
              sx={{
                border: "1px solid #ddd",
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={() => setActionAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleAddToBoard}
          disabled={isAddingToBoard}
          sx={{ gap: 1 }}
        >
          <DashboardCustomizeOutlinedIcon fontSize="small" />
          <ListItemText primary={`Add to Board (${selectedRows.length})`} />
        </MenuItem>
        <MenuItem
          onClick={handleBulkDelete}
          disabled={isDeleting}
          sx={{ gap: 1, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" />
          <ListItemText primary={`Delete (${selectedRows.length})`} />
        </MenuItem>
      </Menu>

      {/* Owner Menu */}
      <Menu
        anchorEl={ownerAnchorEl}
        open={Boolean(ownerAnchorEl)}
        onClose={() => {
          setOwnerAnchorEl(null);
          setOwnerSearchQuery("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 300,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            placeholder={t("Search...")}
            value={ownerSearchQuery}
            onChange={(e) => setOwnerSearchQuery(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "14px",
              },
            }}
          />
        </Box>
        <MenuItem
          onClick={() => setSelectedOwners([])}
          sx={{ py: 1, display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Checkbox size="small" checked={selectedOwners.length === 0} />
          <ListItemText
            primary={t("All")}
            primaryTypographyProps={{ fontSize: "14px" }}
          />
        </MenuItem>
        <Divider />
        <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
          {projectMembers
            .filter((member: any) =>
              (member.fullName || member.email || "")
                .toLowerCase()
                .includes(ownerSearchQuery.toLowerCase()),
            )
            .map((member: any, index: number) => (
              <MenuItem
                key={member._id}
                onClick={() => handleOwnerFilterChange(member._id)}
                sx={{
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: "14px",
                    fontWeight: 600,
                    backgroundColor: colorPalette[index % colorPalette.length],
                  }}
                >
                  {getInitials(member.fullName || member.email)}
                </Avatar>
                <ListItemText
                  primary={member.fullName || member.email}
                  secondary={member.email}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontWeight: 500,
                    sx: { maxWidth: 160 },
                  }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { maxWidth: 160, fontSize: "12px" },
                  }}
                />
                <Checkbox
                  size="small"
                  checked={selectedOwners.includes(member._id)}
                  sx={{ ml: "auto" }}
                />
              </MenuItem>
            ))}
        </Box>
      </Menu>

      {/* Level Menu */}
      <Menu
        anchorEl={levelAnchorEl}
        open={Boolean(levelAnchorEl)}
        onClose={() => setLevelAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => setSelectedLevels([])}>
          <Checkbox
            size="small"
            checked={selectedLevels.length === 0}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={t("All")} />
        </MenuItem>
        <Divider />
        {filterOptions.levels.map((level) => (
          <MenuItem key={level} onClick={() => handleLevelFilterChange(level)}>
            <Checkbox
              size="small"
              checked={selectedLevels.includes(level)}
              sx={{ mr: 1 }}
            />
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                backgroundColor: getLevelColor(
                  Object.keys(severityToLevel).find(
                    (key) => severityToLevel[key] === level,
                  ) || "",
                ),
                mr: 1,
              }}
            />
            <ListItemText primary={level} />
          </MenuItem>
        ))}
      </Menu>

      {/* Group By Menu (Live Feed) */}
      {/* <Menu
        anchorEl={groupByAnchorEl}
        open={Boolean(groupByAnchorEl)}
        onClose={() => setGroupByAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 0.5,
          },
        }}
      >
        <MenuItem onClick={() => handleGroupByFilterChange("")}>
          <Checkbox
            size="small"
            checked={groupByFilter === ""}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={t("None")} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleGroupByFilterChange("errorType")}>
          <Checkbox
            size="small"
            checked={groupByFilter === "errorType"}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={t("Error Type")} />
        </MenuItem>
        <MenuItem onClick={() => handleGroupByFilterChange("ipAddress")}>
          <Checkbox
            size="small"
            checked={groupByFilter === "ipAddress"}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={t("IP Address")} />
        </MenuItem>
        <MenuItem onClick={() => handleGroupByFilterChange("sessionId")}>
          <Checkbox
            size="small"
            checked={groupByFilter === "sessionId"}
            sx={{ mr: 1 }}
          />
          <ListItemText primary={t("Session ID")} />
        </MenuItem>
      </Menu> */}

      {/* Project Selector Menu */}
      <Menu
        anchorEl={projectAnchorEl}
        open={openProjectMenu}
        onClose={handleProjectClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Grid
          container
          spacing={0}
          sx={{
            width: 500,
            height: 300,
            p: 1,
            px: 2,
          }}
        >
          <Grid item xs={3.5}>
            {menuItems.map((item) => (
              <MenuItem
                key={item.name}
                onClick={() => handleProjectSelect(item.name)}
                sx={{
                  width: "130px",
                  px: 0.2,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "14px",
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                }}
                disabled={item.disabled}
              >
                {item.icon}&nbsp; &nbsp;
                {item.name}
              </MenuItem>
            ))}
          </Grid>
          <Grid
            item
            xs={8.5}
            sx={{
              background: hexToRgba(theme.palette.primary.main, 0.15),
              borderRadius: 2,
              p: 1,
              px: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" fontWeight={600}>
                {t("My Projects")}
              </Typography>
              <Typography
                onClick={() => {
                  navigate("/defect-tracker/projects");
                  handleProjectClose();
                }}
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                }}
              >
                {t("Show all projects")}
              </Typography>
            </Box>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder={t("Search Bugs...")}
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 1,
                mb: 0.5,
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#121212" : "#ffffff",
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            {!isLoadingProjects && !isFetchingProjects ? (
              <Box
                ref={projectListRef}
                sx={{
                  maxHeight: 180,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                {!projectSearch ? (
                  <>
                    {getRecentProjects().length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ px: 1, pt: 1, color: "text.secondary" }}
                        >
                          {t("Recent Projects")}
                        </Typography>
                        {getRecentProjects().map((project) => (
                          <MenuItem
                            key={`recent-${project._id}`}
                            onClick={() => handleProjectSelect(project)}
                            sx={{
                              px: 0.5,
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {project.name}
                          </MenuItem>
                        ))}
                      </>
                    )}
                    {getNonRecentProjects().length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ px: 1, pt: 1, color: "text.secondary" }}
                        >
                          {t("allProjects")}
                        </Typography>
                        {getNonRecentProjects().map((project: any) => (
                          <MenuItem
                            key={project._id}
                            onClick={() => handleProjectSelect(project)}
                            sx={{
                              px: 0.5,
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {project.name}
                          </MenuItem>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  projects
                    .filter((project: any) =>
                      project.name
                        .toLowerCase()
                        .includes(projectSearch.toLowerCase()),
                    )
                    .map((project: any) => (
                      <MenuItem
                        key={project._id}
                        onClick={() => handleProjectSelect(project)}
                        sx={{
                          px: 0.5,
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        {project.name}
                      </MenuItem>
                    ))
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Menu>

      {/* tabs for Live Feed and Record */}
      <Tabs value={topTabValue} onChange={handleTopTabChange} sx={{ px: 2 }}>
        <Tab label="Live Feed" />
        <Tab label={t("Record")} />
      </Tabs>

      {/* Main Content Area */}
      <TabContext value={String(topTabValue)}>
        <TabPanel value="0" sx={{ p: 0 }}>
          <LiveFeed
            bugs={systemBugs}
            groupByFilter={groupByFilter}
          />
        </TabPanel>
        <TabPanel
          value="1"
          sx={{
            p: 0,
            height: `calc(${dynamicHeight} - 60px)`,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              px: 2,
              pt: 2,
              overflow: "hidden",
              height: `calc(${dynamicHeight} - 60px)`,
            }}
          >
            <DataGridPro
              getRowId={(row) => row._id}
              columns={columns}
              rows={systemBugs}
              rowCount={totalCount}
              loading={isBugsLoading || isBugsFetching}
              disableColumnMenu
              pagination
              paginationMode="server"
              paginationModel={{
                page: paginationModel.page - 1,
                pageSize: paginationModel.pageSize,
              }}
              onPaginationModelChange={(model) => {
                setPaginationModel({
                  page: model.page + 1,
                  pageSize: model.pageSize,
                });
              }}
              pageSizeOptions={[10, 20, 25, 50, 100]}
              checkboxSelection
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRows(newSelection as string[]);
              }}
              disableMultipleColumnsSorting
              localeText={localeText}
              onRowClick={(params, event) => {
                // Prevent navigation when clicking checkbox
                if (
                  (event.target as HTMLElement).closest(".MuiCheckbox-root")
                ) {
                  return;
                }
                handleRowClick(params.row._id);
              }}
              slots={{
                toolbar: () => (
                  <GridCustomToolbar
                    value={searchValue}
                    onChange={(e) => requestSearch(e.target.value)}
                  />
                ),
                noRowsOverlay: () => (
                  <GridOverlay>
                    <div>No Data Found</div>
                  </GridOverlay>
                ),
                loadingOverlay: () => (
                  <GridOverlay>
                    <CircularProgress />
                  </GridOverlay>
                ),
              }}
              rowHeight={52}
              sx={{
                cursor: "pointer",
                flex: 1,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default SystemBugList;
